import { Component, computed, effect, inject, input, output, signal, untracked } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { rxResource, takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { startWith } from "rxjs";
import { NzCardComponent } from "ng-zorro-antd/card";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzGridModule } from "ng-zorro-antd/grid";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzInputNumberModule } from "ng-zorro-antd/input-number";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzAlertModule } from "ng-zorro-antd/alert";
import { NzCollapseModule } from "ng-zorro-antd/collapse";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzSwitchModule } from "ng-zorro-antd/switch";
import { NzTooltipDirective } from "ng-zorro-antd/tooltip";
import { Job } from "../../../../models/job";
import { CachedProjectService } from "../../../../shared/services/cached-project.service";
import { FieldSection, JOB_CONFIG_DERIVED, JOB_CONFIG_FIELDS, JOB_CONFIG_GROUPS, JOB_CONFIG_SECTIONS, JobConfigField } from "./job-config-fields";

import { CommonModule } from "@angular/common";
import { AWS_GRAVITON_FAMILY_OPTIONS, AWS_GRAVITON_INSTANCE_TYPES, AWS_NONGRAVITON_FAMILY_OPTIONS, AWS_NONGRAVITON_INSTANCE_TYPES } from "./aws_instance_types";
import { HttpErrorResponse } from "@angular/common/http";

type FieldItem =
  | { kind: 'standalone'; field: JobConfigField }
  | { kind: 'group';      parent: JobConfigField; children: JobConfigField[]; childrenHeader: string }
  | { kind: 'section';    label: string;          fields: JobConfigField[] };

@Component({
  selector: "app-job-config",
  templateUrl: "./job-config.component.html",
  styleUrl: "./job-config.component.less",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzCardComponent,
    NzFormModule,
    NzGridModule,
    NzInputModule,
    NzInputNumberModule,
    NzAlertModule,
    NzCollapseModule,
    NzIconModule,
    NzSelectModule,
    NzSwitchModule,
    NzTooltipDirective,
  ],
})
export class JobConfigComponent {
  private cachedProjectService = inject(CachedProjectService);
  private fb = inject(FormBuilder);

  readonly job = input<Job | null>(null);
  readonly jobPath = input<string>('');
  readonly configChange = output<Record<string, any>>();
  readonly validityChange = output<boolean>();

  protected missingFields = signal<JobConfigField[]>([]);
  protected showMissingAlert = computed(() => this.missingFields().length > 0);

  protected xwiExecPath = signal<string>('');
  protected readonly dynamicOptionGroups = computed<Record<string, { label: string; options: { value: any; label: string }[] }[]>>(() => {
    const isGraviton = this.xwiExecPath().includes('aarch64');
    const groups = [
      { label: 'Instance Families', options: isGraviton ? AWS_GRAVITON_FAMILY_OPTIONS : AWS_NONGRAVITON_FAMILY_OPTIONS },
      { label: 'Instance Types',    options: isGraviton ? AWS_GRAVITON_INSTANCE_TYPES : AWS_NONGRAVITON_INSTANCE_TYPES },
    ];
    return { scheduler_instance_type: groups, worker_instance_type: groups };
  });
  protected missingFieldLabels = computed(() =>
    this.missingFields().map((f) => f.label).join(', ')
  );

  protected readonly fieldItems: FieldItem[] = (() => {
    const childKeySet    = new Set(JOB_CONFIG_GROUPS.flatMap((g) => g.childKeys ?? []));
    const groupMap       = new Map(JOB_CONFIG_GROUPS.map((g) => [g.parentKey, g.childKeys ?? []]));
    const fieldByKey     = new Map(JOB_CONFIG_FIELDS.map((f) => [f.key, f]));
    const sectionByKey   = new Map<string, FieldSection>();
    const emittedSections = new Set<string>();
    for (const s of JOB_CONFIG_SECTIONS) {
      for (const k of s.fieldKeys) sectionByKey.set(k, s);
    }

    const items: FieldItem[] = [];
    for (const field of JOB_CONFIG_FIELDS.filter((f) => !f.hidden)) {
      if (childKeySet.has(field.key)) continue;

      const section = sectionByKey.get(field.key);
      if (section) {
        if (!emittedSections.has(section.label)) {
          emittedSections.add(section.label);
          items.push({
            kind: 'section',
            label: section.label,
            fields: section.fieldKeys.map((k) => fieldByKey.get(k)!).filter((f) => f && !f.hidden),
          });
        }
        continue;
      }

      const childKeys = groupMap.get(field.key);
      if (childKeys) {
        const children = childKeys.map((k) => fieldByKey.get(k)!).filter(Boolean);
        items.push({
          kind: 'group',
          parent: field,
          children,
          childrenHeader: children.map((c) => c.label).join(', ') + ' — override individually',
        });
      } else {
        items.push({ kind: 'standalone', field });
      }
    }
    return items;
  })();

  protected form: FormGroup = this.fb.group(
    Object.fromEntries(
      JOB_CONFIG_FIELDS.map((f) => [
        f.key,
        [f.default ?? null, f.required ? Validators.required : []],
      ])
    )
  );

  protected jobConfigResource = rxResource<{ [key: string]: any }, { id: string } | undefined>({
    params: computed(() => {
      const job = this.job();
      return job ? { id: job.id } : undefined;
    }),
    stream: ({ params }) => this.cachedProjectService.getJobConfig(params.id),
  });

  protected jobConfigErrorMessage = computed(() => (this.jobConfigResource.error()?.cause as HttpErrorResponse).error?.detail ?? "An Unexpected error occurred.")

  private _prevFormValue: Record<string, any> = {};

  constructor() {
    effect(() => {
      const content = this.jobConfigResource.value();
      if (!content) return;
      const parsed = content;
      const patch: Record<string, any> = {};
      for (const field of JOB_CONFIG_FIELDS) {
        if (field.inferred && field.key in parsed) {
          patch[field.key] = parsed[field.key];
        }
      }
      this.form.patchValue(patch);
      const jobPath = untracked(() => this.jobPath());
      // Apply form-dep derivations now that inferred values are available
      const derivedPatch: Record<string, any> = {};
      for (const rule of JOB_CONFIG_DERIVED) {
        if (!rule.formDeps) continue;
        const value = rule.compute(this.form.value, jobPath);
        if (value != null) derivedPatch[rule.targetKey] = value;
      }
      if (Object.keys(derivedPatch).length > 0) {
        this.form.patchValue(derivedPatch, { emitEvent: false });
      }

      const missing = JOB_CONFIG_FIELDS.filter(
        (f) => f.hidden && f.required &&
               (this.form.get(f.key)?.value == null || this.form.get(f.key)?.value === '')
      );
      this.missingFields.set(missing);

      // The derived patch above uses { emitEvent: false }, which updates form.valid
      // but suppresses statusChanges; re-emit validity so the parent stays in sync.
      this.validityChange.emit(this.form.valid);
    });

    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      const current = this.form.value;
      const patch: Record<string, any> = {};

      // Group propagation (parent → children)
      for (const { parentKey, childKeys } of JOB_CONFIG_GROUPS) {
        if (current[parentKey] !== this._prevFormValue[parentKey]) {
          childKeys?.forEach((k) => (patch[k] = current[parentKey]));
        }
      }

      // Form-dep derived fields
      for (const rule of JOB_CONFIG_DERIVED) {
        if (!rule.formDeps) continue;
        const depChanged = rule.formDeps.some(dep => current[dep] !== this._prevFormValue[dep]);
        if (!depChanged) continue;
        const value = rule.compute(current, this.jobPath());
        if (value != null) patch[rule.targetKey] = value;
      }

      if (Object.keys(patch).length > 0) {
        this.form.patchValue(patch, { emitEvent: false });
      }

      // Reset instance types if the executable architecture changed
      const prevExec: string = this._prevFormValue.xwi_exec_path ?? '';
      const currExec: string = current.xwi_exec_path ?? '';
      if (prevExec !== '' && prevExec !== currExec && prevExec.includes('aarch64') !== currExec.includes('aarch64')) {
        this.form.patchValue(
          { scheduler_instance_type: null, worker_instance_type: null },
          { emitEvent: false }
        );
      }

      this._prevFormValue = this.form.value;
      this.xwiExecPath.set(currExec);
      this.configChange.emit(this.form.value);
      // Group/derived patches above use { emitEvent: false }; re-emit validity
      // since statusChanges won't fire for those silent updates.
      this.validityChange.emit(this.form.valid);
    });

    // jobPath-dep derived fields (output_path, environment_variables, xwi_runfile)
    effect(() => {
      const path = this.jobPath();
      const patch: Record<string, any> = {};

      for (const rule of JOB_CONFIG_DERIVED) {
        if (!rule.jobPathDep) continue;
        const value = rule.compute(this.form.value, path);
        if (value == null) continue;
        patch[rule.targetKey] = value;
        // Propagate to group children (e.g. output_path → checkpoint_path, dumps_path)
        const group = JOB_CONFIG_GROUPS.find(g => g.parentKey === rule.targetKey);
        group?.childKeys?.forEach(k => { patch[k] = value; });
      }

      if (Object.keys(patch).length === 0) return;
      this.form.patchValue(patch, { emitEvent: false });
      Object.assign(this._prevFormValue, patch);
      this.configChange.emit(this.form.value);
      // Required fields (model_path, checkpoint_path, dumps_path) are filled here
      // via { emitEvent: false }; re-emit validity so the Prep button isn't left
      // disabled when this resolves after the job-config load.
      this.validityChange.emit(this.form.valid);
    });

    this.form.statusChanges.pipe(startWith(this.form.status), takeUntilDestroyed()).subscribe(() => {
      this.validityChange.emit(this.form.valid);
    });
  }

}
